// src/pages/subscription/SubscriptionPage.js
import React, { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import { toast } from "react-toastify";
import {
    CANCEL_SUBSCRIPTION,
    CREATE_CHECKOUT_SESSION,
} from "../../components/subscription/mutations";
import { GET_SUBSCRIPTION_PLANS } from "../../components/subscription/queries";
import { useDispatch, useSelector } from "react-redux";
import { GlobalStyles } from "../../constants/styles";
import { userActions } from "../../store/userAuthSlice";
import { confirmAlert } from "react-confirm-alert";
import { useLocation, useNavigate } from "react-router-dom";
import useActiveProfile from "../../components/common/hooks/useActiveProfile";
import Select from "react-select";
import useThemeColors from "../../components/organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

function getSubscriptionTranslationKey(value) {
    const normalized = String(value || "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

    if (normalized.includes("enterprise")) return "enterprise";
    if (normalized.includes("elite")) return "elite";
    if (normalized.includes("pro")) return "pro";
    if (normalized.includes("trial")) return "trial";
    if (normalized.includes("test")) return "test";
    if (normalized.includes("free")) return "free";

    return normalized;
}

const GET_ORGANIZATIONS = gql`
    query Organizations($profileId: Int!, $page: Int, $pageSize: Int) {
        organizations(profileId: $profileId, page: $page, pageSize: $pageSize) {
            objects {
                id
                name
                slug
                activeSubscription {
                    id
                    plan {
                        id
                        name
                        application
                    }
                }
            }
        }
    }
`;

export default function SubscriptionPage() {
    const scopeId = "kbola-subscription-page";
    const { t } = useTranslation();

    const user = useSelector((state) => state.user.auth.user);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const location = useLocation();
    const defaultAudience =
        location.state?.defaultTab === "organization"
            ? "organization"
            : "individual";

    const colors = useThemeColors();

    const { activeProfile } = useActiveProfile();
    const currentSubscriptions = [user?.appSubscription];
    const currency = (activeProfile?.currency || "usd").toUpperCase();

    const [billingCycle, setBillingCycle] = useState("MONTHLY");
    const [expandedSection, setExpandedSection] = useState(null);
    const [audience, setAudience] = useState(defaultAudience);
    const [featureInfoModal, setFeatureInfoModal] = useState(null);

    const defaultOrgSlug = location.state?.organizationSlug || null;
    const defaultOrgIdFromState = location.state?.organizationId || null;

    const [selectedOrganizationId, setSelectedOrganizationId] = useState(null);

    const [createCheckoutSession, { loading: creating }] =
        useMutation(CREATE_CHECKOUT_SESSION);
    const [cancelSubscription] = useMutation(CANCEL_SUBSCRIPTION);

    useEffect(() => {
        if (audience === "organization") {
            setBillingCycle("YEARLY");
        }
    }, [audience]);

    useEffect(() => {
        if (!featureInfoModal) return undefined;

        const closeOnEscape = (event) => {
            if (event.key === "Escape") {
                setFeatureInfoModal(null);
            }
        };

        window.addEventListener("keydown", closeOnEscape);
        return () => window.removeEventListener("keydown", closeOnEscape);
    }, [featureInfoModal]);

    const { data: plansData } = useQuery(GET_SUBSCRIPTION_PLANS);
    const plans = plansData?.subscriptionPlans || [];
    const appPlans = plans.filter((plan) => plan.application === "app");
    const orgPlans = plans.filter((plan) => plan.application === "web");

    const freeOrgPlanId = useMemo(() => {
        if (!orgPlans || orgPlans.length === 0) return null;

        const freePlan = orgPlans.find((plan) =>
            plan.prices?.some(
                (p) =>
                    p.currency === currency.toLowerCase() &&
                    Number(p.amount) === 0
            )
        );

        return freePlan?.id ?? null;
    }, [orgPlans, currency]);

    const { data: orgsData, loading: orgsLoading } = useQuery(
        GET_ORGANIZATIONS,
        {
            variables: {
                profileId: activeProfile?.id ?? 0,
                page: 1,
                pageSize: 50,
            },
            skip: !activeProfile?.id,
            fetchPolicy: "cache-and-network",
        }
    );

    const myOrganizations = useMemo(
        () => orgsData?.organizations?.objects ?? [],
        [orgsData]
    );

    const orgOptions = useMemo(
        () =>
            myOrganizations.map((org) => ({
                value: org.id,
                label: org.name,
            })),
        [myOrganizations]
    );

    useEffect(() => {
        if (selectedOrganizationId || myOrganizations.length === 0) return;

        let target = null;

        if (defaultOrgSlug) {
            target = myOrganizations.find((org) => org.slug === defaultOrgSlug);
        }

        if (!target && defaultOrgIdFromState) {
            target = myOrganizations.find(
                (org) => String(org.id) === String(defaultOrgIdFromState)
            );
        }

        if (target) {
            setSelectedOrganizationId(target.id);
        }
    }, [
        defaultOrgSlug,
        defaultOrgIdFromState,
        myOrganizations,
        selectedOrganizationId,
    ]);

    const selectedOrganization = useMemo(
        () =>
            myOrganizations.find(
                (org) => String(org.id) === String(selectedOrganizationId)
            ),
        [myOrganizations, selectedOrganizationId]
    );

    const currentOrgPlanId =
        selectedOrganization?.activeSubscription?.plan?.id ?? null;

    const fmt = useMemo(() => {
        try {
            return new Intl.NumberFormat(undefined, {
                style: "currency",
                currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        } catch {
            return new Intl.NumberFormat(undefined, {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        }
    }, [currency]);

    const primary = colors.primary;
    const secondary = colors.secondary || colors.accent || colors.primary;
    const background = colors.background;
    const surface = colors.surface;
    const textOnBg = colors.textOnBackground;
    const textOnSurface = colors.textOnSurface;
    const textOnPrimary = colors.textOnPrimary || colors.woodsmoke || "#000000";
    const borderColor = colors.border || alphaFromHex(textOnBg, 0.12);
    const successColor = colors.success || GlobalStyles.colors.success;
    const warningColor = colors.warning || GlobalStyles.colors.warning;
    const mutedText = colors.muted || alphaFromHex(textOnBg, 0.7);

    const primarySoft = alphaFromHex(primary, 0.12);
    const primarySofter = alphaFromHex(primary, 0.06);
    const primaryGlow = alphaFromHex(primary, 0.4);

    const ui = useMemo(
        () => ({
            page: {
                backgroundColor: background,
                color: textOnBg,
                fontFamily: "Poppins, sans-serif",
                minHeight: "100vh",
                padding: "28px 18px",
            },
            container: {
                maxWidth: 1200,
                margin: "0 auto",
            },
            hero: {
                borderRadius: 18,
                border: `1px solid ${alphaFromHex(textOnBg, 0.08)}`,
                background: `
                    radial-gradient(
                        900px 320px at 10% 0%,
                        ${primarySoft},
                        transparent 58%
                    ),
                    linear-gradient(
                        180deg,
                        ${alphaFromHex(textOnBg, 0.06)},
                        transparent
                    )
                `,
                boxShadow: "0 18px 44px rgba(0,0,0,0.35)",
                padding: 18,
                marginBottom: 16,
            },
            heroTitle: {
                margin: 0,
                fontSize: 22,
                fontWeight: 900,
                color: primary,
                letterSpacing: "0.01em",
            },
            heroSub: {
                margin: "6px 0 0 0",
                fontSize: 13.5,
                fontWeight: 700,
                color: mutedText,
            },

            toggleRow: {
                marginTop: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
            },
            toggle: {
                display: "inline-flex",
                padding: 6,
                borderRadius: 999,
                border: `1px solid ${alphaFromHex(textOnBg, 0.12)}`,
                background: alphaFromHex(surface, 0.6),
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
            },
            toggleBtn: {
                border: "none",
                borderRadius: 999,
                padding: "10px 12px",
                fontWeight: 900,
                cursor: "pointer",
                color: mutedText,
                background: "transparent",
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
            },
            toggleBtnActive: {
                color: textOnPrimary,
                background: primary,
                boxShadow: "0 10px 22px rgba(0,0,0,0.25)",
            },

            tabRow: {
                marginTop: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                flexWrap: "wrap",
            },
            tabStrip: {
                display: "inline-flex",
                padding: 4,
                borderRadius: 999,
                border: `1px solid ${alphaFromHex(textOnBg, 0.10)}`,
                background: alphaFromHex(surface, 0.7),
                margin: "0 auto",
            },
            tabBtn: {
                border: "none",
                borderRadius: 999,
                padding: "8px 14px",
                fontWeight: 900,
                cursor: "pointer",
                color: mutedText,
                background: "transparent",
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 14,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
            },
            tabBtnActive: {
                background: primarySoft,
                color: primary,
                boxShadow: "0 10px 22px rgba(0,0,0,0.25)",
            },

            colTitle: {
                margin: "18px 0 8px 0",
                fontSize: 18,
                fontWeight: 900,
                color: textOnBg,
            },
            colHint: {
                margin: "0 0 12px 0",
                fontSize: 14,
                fontWeight: 700,
                color: mutedText,
            },

            card: {
                borderRadius: 18,
                border: `1px solid ${alphaFromHex(textOnBg, 0.12)}`,
                background: alphaFromHex(surface, 0.9),
                boxShadow: "0 18px 44px rgba(0,0,0,0.30)",
                overflow: "hidden",
                marginBottom: 12,
            },
            cardHeader: {
                padding: "12px 14px",
                background: `linear-gradient(135deg, ${primarySoft}, ${primarySofter})`,
                borderBottom: `1px solid ${alphaFromHex(textOnBg, 0.08)}`,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
            },
            headerLeft: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
            },
            planName: {
                margin: 0,
                fontSize: 18,
                fontWeight: 900,
                color: secondary,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
            },
            headerMeta: {
                display: "inline-flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
            },
            pill: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                border: `1px solid ${alphaFromHex(textOnBg, 0.18)}`,
                background: alphaFromHex(surface, 0.9),
                color: textOnSurface,
                fontWeight: 900,
                fontSize: 12,
                lineHeight: 1,
                whiteSpace: "nowrap",
            },
            pillCurrent: {
                border: `1px solid ${alphaFromHex(successColor, 0.35)}`,
                background: alphaFromHex(successColor, 0.18),
                color: successColor,
            },
            pillBest: {
                border: `1px solid ${alphaFromHex(warningColor, 0.35)}`,
                background: alphaFromHex(warningColor, 0.18),
                color: warningColor,
            },
            priceTag: {
                fontWeight: 900,
                color: textOnSurface,
                fontSize: 13,
                whiteSpace: "nowrap",
            },

            cardBody: {
                padding: 14,
                background: alphaFromHex(surface, 0.95),
            },
            desc: {
                margin: "0 0 10px 0",
                color: textOnSurface,
                fontWeight: 700,
                fontSize: 13.5,
                lineHeight: 1.4,
            },
            bigPrice: {
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 10,
            },
            bigPriceLeft: {
                fontSize: 18,
                fontWeight: 900,
                color: textOnSurface,
            },
            save: {
                fontSize: 12.5,
                fontWeight: 900,
                color: primary,
                background: primarySoft,
                border: `1px solid ${alphaFromHex(primary, 0.22)}`,
                borderRadius: 999,
                padding: "6px 10px",
            },

            featureList: {
                listStyle: "none",
                padding: 0,
                margin: "8px 0 0 0",
                display: "grid",
                gap: 8,
            },
            featureItem: {
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                color: textOnSurface,
                fontWeight: 700,
                fontSize: 13.5,
            },
            featureLabel: {
                flex: 1,
                minWidth: 0,
            },
            featureInfoButton: {
                border: "none",
                background: "transparent",
                color: mutedText,
                cursor: "pointer",
                padding: "0 2px",
                lineHeight: 1,
                marginTop: 1,
            },
            modalBackdrop: {
                position: "fixed",
                inset: 0,
                zIndex: 1050,
                background: "rgba(0,0,0,0.62)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 18,
            },
            modalCard: {
                width: "min(520px, 100%)",
                borderRadius: 18,
                border: `1px solid ${alphaFromHex(textOnBg, 0.14)}`,
                background: surface,
                color: textOnSurface,
                boxShadow: "0 24px 70px rgba(0,0,0,0.45)",
                overflow: "hidden",
            },
            modalHeader: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "14px 16px",
                borderBottom: `1px solid ${alphaFromHex(textOnSurface, 0.10)}`,
                background: primarySoft,
            },
            modalTitle: {
                margin: 0,
                color: primary,
                fontSize: 16,
                fontWeight: 900,
            },
            modalClose: {
                border: "none",
                background: "transparent",
                color: textOnSurface,
                cursor: "pointer",
                fontSize: 22,
                lineHeight: 1,
                padding: "2px 4px",
            },
            modalBody: {
                padding: "16px",
                whiteSpace: "pre-line",
                color: textOnSurface,
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.65,
            },
            stars: {
                minWidth: 72,
                display: "inline-flex",
                alignItems: "center",
                gap: 2,
                marginTop: 1,
            },
            starOn: { color: warningColor },
            starOff: { color: alphaFromHex(textOnSurface, 0.25) },

            actions: {
                marginTop: 12,
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: `1px solid ${alphaFromHex(textOnSurface, 0.18)}`,
                background: alphaFromHex(surface, 0.98),
                color: textOnSurface,
                cursor: "pointer",
                lineHeight: 1,
            },
            btnPrimary: {
                background: primary,
                border: `1px solid ${alphaFromHex(primary, 0.6)}`,
                color: textOnPrimary,
            },
            btnDangerGhost: {
                background: alphaFromHex(GlobalStyles.colors.danger, 0.1),
                border: `1px solid ${alphaFromHex(
                    GlobalStyles.colors.danger,
                    0.28
                )}`,
                color: GlobalStyles.colors.danger,
            },
            btnDisabled: { opacity: 0.65, cursor: "not-allowed" },

            note: {
                marginTop: 18,
                borderRadius: 18,
                border: `1px solid ${alphaFromHex(textOnBg, 0.12)}`,
                background: `linear-gradient(135deg, ${alphaFromHex(
                    surface,
                    0.85
                )}, ${alphaFromHex(surface, 0.95)})`,
                boxShadow: "0 18px 44px rgba(0,0,0,0.30)",
                overflow: "hidden",
            },
            noteHead: {
                padding: "12px 14px",
                borderBottom: `1px solid ${alphaFromHex(textOnBg, 0.08)}`,
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: primarySoft,
            },
            noteTitle: {
                margin: 0,
                fontWeight: 900,
                fontSize: 14,
                color: primary,
            },
            noteBody: {
                padding: 14,
                color: textOnSurface,
                fontWeight: 700,
                lineHeight: 1.5,
            },

            orgSelectorWrap: {
                marginBottom: 18,
                padding: 10,
                borderRadius: 16,
                border: `1px solid ${alphaFromHex(textOnBg, 0.12)}`,
                background: alphaFromHex(surface, 0.9),
                display: "flex",
                alignItems: "stretch",
                gap: 12,
                flexWrap: "wrap",
            },
            orgSelectorLeft: {
                flex: 1,
                minWidth: 0,
            },
            orgSelectorBadge: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 10px",
                borderRadius: 999,
                background: primarySoft,
                border: `1px solid ${alphaFromHex(primary, 0.32)}`,
                color: primary,
                fontSize: 14,
                fontWeight: 900,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
            },
            orgSelectorLabel: {
                display: "block",
                marginTop: 8,
                fontSize: 16,
                fontWeight: 800,
                color: textOnSurface,
            },
            orgSelectorHint: {
                margin: "4px 0 0 0",
                fontSize: 14,
                fontWeight: 600,
                color: mutedText,
            },
            orgSelectorRight: {
                flexBasis: 260,
                flexGrow: 0,
                flexShrink: 0,
                minWidth: 220,
            },
            orgSelectorControl: {
                position: "relative",
                borderRadius: 999,
                overflow: "hidden",
                background: surface,
                border: `1px solid ${borderColor}`,
                boxShadow: "0 10px 24px rgba(0,0,0,0.35)",
            },
            orgSelectorControlActive: {
                borderColor: primary,
                boxShadow: `0 0 0 2px ${primaryGlow}`,
            },
            orgSelectorIcon: {
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: 12,
                color: mutedText,
                pointerEvents: "none",
            },
            orgSelectorDisabled: {
                opacity: 0.55,
                cursor: "not-allowed",
            },
        }),
        [
            background,
            textOnBg,
            surface,
            primary,
            primarySoft,
            primarySofter,
            primaryGlow,
            textOnSurface,
            textOnPrimary,
            mutedText,
            warningColor,
            successColor,
            borderColor,
            secondary
        ]
    );

    const handleSubscribe = async (plan, organizationId = null) => {
        const isOrgPlan = plan.application === "web";
        const effectiveBillingCycle = isOrgPlan ? "YEARLY" : billingCycle;

        const selectedPrice = plan.prices.find(
            (p) =>
                p.currency === currency.toLowerCase() &&
                p.billingCycle === effectiveBillingCycle
        );

        if (!selectedPrice) {
            toast.error(t("subscriptionPage.plans.noPriceAvailable"));
            return;
        }

        if (isOrgPlan && !organizationId) {
            toast.error(t("subscriptionPage.plans.orgRequired"));
            return;
        }

        try {
            const { data } = await createCheckoutSession({
                variables: {
                    priceId: selectedPrice.stripePriceId,
                    successUrl: "https://kbolasport.com/subscription-success",
                    cancelUrl: "https://kbolasport.com/subscription-cancel",
                    organizationId: isOrgPlan ? Number(organizationId) : null,
                },
            });

            const url = data?.createCheckoutSession?.checkoutUrl;
            if (url) {
                dispatch(userActions.logout());
                window.location.href = url;
            } else {
                toast.error(t("subscriptionPage.plans.checkoutFailed"));
            }
        } catch (error) {
            toast.error(getLocalizedGraphQLError(error, t, "errors.somethingWentWrong"));
        }
    };

    const cancelSubscriptionHandler = async (plan) => {
        try {
            const mySubscription = currentSubscriptions.find(
                (s) => s?.plan?.id === plan.id
            );
            const { data } = await cancelSubscription({
                variables: {
                    stripeSubscriptionId: mySubscription.stripeSubscriptionId,
                },
            });

            if (data?.cancelSubscription?.success) {
                dispatch(userActions.logout());
                return navigate("/");
            } else {
                toast.error(t("subscriptionPage.plans.cancelFailed"));
            }
        } catch (error) {
            toast.error(t("subscriptionPage.plans.cancelFailed"));
        }
    };

    const cancelSubscriptionConfirm = async (plan) => {
        confirmAlert({
            title: t("subscriptionPage.confirmCancel.title"),
            message: t("subscriptionPage.confirmCancel.message"),
            buttons: [
                {
                    label: t("subscriptionPage.confirmCancel.yes"),
                    onClick: async () => await cancelSubscriptionHandler(plan),
                },
                { label: t("subscriptionPage.confirmCancel.no") },
            ],
        });
    };

    const renderStars = (level) => {
        const total = 3;
        return (
            <span
                style={ui.stars}
                aria-label={t("subscriptionPage.accessibility.featureLevel", { level, total })}
            >
                {[...Array(total)].map((_, i) => (
                    <i
                        key={i}
                        className="fas fa-star"
                        style={i < level ? ui.starOn : ui.starOff}
                    />
                ))}
            </span>
        );
    };

    const getTranslatedPlanName = (plan) => {
        const planKey = getSubscriptionTranslationKey(plan?.name);
        return t(`subscriptionPage.options.plans.${planKey}`, {}, plan?.name || "");
    };

    const getTranslatedFeatureLabel = (feature) =>
        t(
            `subscriptionPage.options.features.${feature?.key}.label`,
            {},
            feature?.label || ""
        );

    const getTranslatedFeatureDescription = (feature) =>
        t(
            `subscriptionPage.options.features.${feature?.key}.description`,
            {},
            feature?.description || ""
        );

    const openFeatureDescription = (feature) => {
        const description = getTranslatedFeatureDescription(feature);

        if (!description) return;

        setFeatureInfoModal({
            title: getTranslatedFeatureLabel(feature),
            description,
        });
    };

    const priceLabel = (selectedPrice, currentType) => {
        if (!selectedPrice || selectedPrice.amount === "0.00") return "";

        const effectiveBillingCycle =
            currentType === "web" ? "YEARLY" : billingCycle;

        const suffix =
            effectiveBillingCycle === "MONTHLY"
                ? t("subscriptionPage.billingSuffix.month")
                : t("subscriptionPage.billingSuffix.year");

        return `${fmt.format(selectedPrice.amount)} ${suffix}`;
    };

    const renderPlans = (
        plansToRender,
        currentType,
        currentOrgPlanIdParam = null
    ) => (
        <div className="accordion" id={`accordion-${currentType}`}>
            {plansToRender.map((plan, index) => {
                const effectiveBillingCycle =
                    currentType === "web" ? "YEARLY" : billingCycle;

                const selectedPrice = plan.prices.find(
                    (p) =>
                        p.currency === currency.toLowerCase() &&
                        p.billingCycle === effectiveBillingCycle
                );

                const effectiveOrgCurrentPlanId =
                    currentType === "web"
                        ? currentOrgPlanIdParam ||
                        (!selectedOrganization?.activeSubscription && freeOrgPlanId)
                        : null;

                const isCurrent =
                    currentType === "app"
                        ? currentSubscriptions.some(
                            (sub) =>
                                sub?.plan?.id === plan.id &&
                                sub?.plan?.application === currentType
                        )
                        : currentType === "web"
                            ? effectiveOrgCurrentPlanId &&
                            String(effectiveOrgCurrentPlanId) === String(plan.id)
                            : false;

                const planTranslationKey = getSubscriptionTranslationKey(plan.name);
                const isBestValue = planTranslationKey === "elite";
                const isTrial = planTranslationKey === "trial";

                const monthlyPrice = plan.prices.find(
                    (p) =>
                        p.billingCycle === "MONTHLY" &&
                        p.currency === currency.toLowerCase()
                );

                const yearlyPrice = plan.prices.find(
                    (p) =>
                        p.billingCycle === "YEARLY" &&
                        p.currency === currency.toLowerCase()
                );

                const discountPercent =
                    monthlyPrice && yearlyPrice
                        ? Math.round(
                            100 - (yearlyPrice.amount / (monthlyPrice.amount * 12)) * 100
                        )
                        : 0;

                const collapseId = `${currentType}-collapse-${index}`;
                const isOpen = expandedSection === collapseId;

                const requiresOrg = currentType === "web";
                const orgNotChosen = requiresOrg && !selectedOrganizationId;

                return (
                    (!isTrial || (isTrial && isCurrent)) && (
                        <div
                            key={plan.id}
                            style={{
                                ...ui.card,
                                borderColor: isCurrent ? primarySoft : ui.card.border,
                            }}
                            className="kbola-plan-card"
                        >
                            <div
                                style={ui.cardHeader}
                                onClick={() => setExpandedSection(isOpen ? null : collapseId)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        setExpandedSection(isOpen ? null : collapseId);
                                    }
                                }}
                            >
                                <div style={ui.headerLeft}>
                                    <h4 style={ui.planName}>
                                        {getTranslatedPlanName(plan)}
                                    </h4>
                                    <div style={ui.headerMeta}>
                                        {isCurrent && (
                                            <span style={{ ...ui.pill, ...ui.pillCurrent }}>
                                                <i className="fas fa-check" />{" "}
                                                {t("subscriptionPage.plans.current")}
                                            </span>
                                        )}
                                        {isBestValue && (
                                            <span style={{ ...ui.pill, ...ui.pillBest }}>
                                                <i className="fas fa-bolt" />{" "}
                                                {t("subscriptionPage.plans.bestValue")}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                    }}
                                >
                                    <span style={ui.priceTag}>
                                        {priceLabel(selectedPrice, currentType)}
                                    </span>
                                    <span style={ui.pill}>
                                        <i
                                            className={`fas ${
                                                isOpen ? "fa-chevron-up" : "fa-chevron-down"
                                            }`}
                                        />
                                    </span>
                                </div>
                            </div>

                            <div
                                id={collapseId}
                                className={`collapse ${isOpen ? "show" : ""}`}
                            >
                                <div style={ui.cardBody}>
                                    <p style={ui.desc}>{plan.description}</p>

                                    <div style={ui.bigPrice}>
                                        <div style={ui.bigPriceLeft}>
                                            {priceLabel(selectedPrice, currentType)}
                                        </div>
                                        {effectiveBillingCycle === "YEARLY" &&
                                        discountPercent > 0 ? (
                                            <div style={ui.save}>
                                                {t("subscriptionPage.plans.savePercent", {
                                                    percent: discountPercent,
                                                })}
                                            </div>
                                        ) : null}
                                    </div>

                                    <ul style={ui.featureList}>
                                        {plan.features?.map((feature) => (
                                            <li
                                                key={feature.key}
                                                style={ui.featureItem}
                                            >
                                                {renderStars(feature.level)}
                                                <span style={ui.featureLabel}>
                                                    {getTranslatedFeatureLabel(feature)}
                                                </span>
                                                <button
                                                    type="button"
                                                    style={ui.featureInfoButton}
                                                    title={getTranslatedFeatureDescription(feature)}
                                                    aria-label={getTranslatedFeatureDescription(feature)}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        openFeatureDescription(feature);
                                                    }}
                                                >
                                                    <i className="fas fa-info-circle" />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>

                                    {selectedPrice && selectedPrice.amount > 0 && (
                                        <div style={ui.actions}>
                                            {!isCurrent && !plan.isTrial ? (
                                                <button
                                                    type="button"
                                                    style={{
                                                        ...ui.btn,
                                                        ...ui.btnPrimary,
                                                        ...((creating ||
                                                                !selectedPrice ||
                                                                orgNotChosen) &&
                                                            ui.btnDisabled),
                                                    }}
                                                    disabled={
                                                        creating ||
                                                        !selectedPrice ||
                                                        orgNotChosen
                                                    }
                                                    onClick={() =>
                                                        handleSubscribe(
                                                            plan,
                                                            currentType === "web"
                                                                ? selectedOrganizationId
                                                                : null
                                                        )
                                                    }
                                                >
                                                    <i className="fas fa-credit-card" />
                                                    {creating
                                                        ? t("subscriptionPage.plans.processing")
                                                        : requiresOrg && orgNotChosen
                                                            ? t("subscriptionPage.plans.selectOrganization")
                                                            : t("subscriptionPage.plans.subscribe")}
                                                </button>
                                            ) : !plan.isTrial && currentType === "app" ? (
                                                <button
                                                    type="button"
                                                    style={{
                                                        ...ui.btn,
                                                        ...ui.btnDangerGhost,
                                                        ...(creating ? ui.btnDisabled : null),
                                                    }}
                                                    disabled={creating}
                                                    onClick={() =>
                                                        cancelSubscriptionConfirm(plan)
                                                    }
                                                >
                                                    <i className="fas fa-ban" />
                                                    {creating
                                                        ? t("subscriptionPage.plans.processing")
                                                        : t("subscriptionPage.plans.cancelSubscription")}
                                                </button>
                                            ) : null}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )
                );
            })}
        </div>
    );

    const hasOrgSelected = !!selectedOrganizationId;

    const focusRing = alphaFromHex(primary, 0.18);
    const planHoverShadow = "0 22px 56px rgba(0,0,0,0.40)";

    return (
        <div className="content-wrapper" id={scopeId} style={ui.page}>
            <div style={ui.container}>
                <section style={ui.hero}>
                    <h1 style={ui.heroTitle}>{t("subscriptionPage.title")}</h1>
                    <p style={ui.heroSub}>
                        {t("subscriptionPage.subtitle", { currency })}
                    </p>

                    <div style={ui.toggleRow}>
                        <div
                            style={ui.toggle}
                            role="tablist"
                            aria-label="Billing cycle toggle"
                        >
                            {audience === "individual" && (
                                <>
                                    <button
                                        type="button"
                                        style={{
                                            ...ui.toggleBtn,
                                            ...(billingCycle === "MONTHLY"
                                                ? ui.toggleBtnActive
                                                : null),
                                        }}
                                        onClick={() => setBillingCycle("MONTHLY")}
                                        aria-pressed={billingCycle === "MONTHLY"}
                                    >
                                        <i className="fas fa-calendar-alt" />
                                        {t("subscriptionPage.billing.monthly")}
                                    </button>
                                    <button
                                        type="button"
                                        style={{
                                            ...ui.toggleBtn,
                                            ...(billingCycle === "YEARLY"
                                                ? ui.toggleBtnActive
                                                : null),
                                        }}
                                        onClick={() => setBillingCycle("YEARLY")}
                                        aria-pressed={billingCycle === "YEARLY"}
                                    >
                                        <i className="fas fa-calendar-check" />
                                        {t("subscriptionPage.billing.yearly")}
                                    </button>
                                </>
                            )}

                            {audience === "organization" && (
                                <button
                                    type="button"
                                    style={{
                                        ...ui.toggleBtn,
                                        ...ui.toggleBtnActive,
                                        cursor: "default",
                                    }}
                                    disabled
                                >
                                    <i className="fas fa-calendar-check" />
                                    {t("subscriptionPage.billing.yearlyOnly")}
                                </button>
                            )}
                        </div>

                        <div
                            style={{
                                ...ui.pill,
                                border: `1px solid ${alphaFromHex(successColor, 0.28)}`,
                                color: successColor,
                                background: primarySoft,
                            }}
                        >
                            <i className="fas fa-coins" />
                            {t("subscriptionPage.billing.currency")}: {currency}
                        </div>
                    </div>

                    <div style={ui.tabRow}>
                        <div
                            style={ui.tabStrip}
                            role="tablist"
                            aria-label="Subscription audience toggle"
                        >
                            <button
                                type="button"
                                style={{
                                    ...ui.tabBtn,
                                    ...(audience === "individual"
                                        ? ui.tabBtnActive
                                        : null),
                                }}
                                onClick={() => setAudience("individual")}
                                aria-pressed={audience === "individual"}
                            >
                                <i className="fas fa-user" />
                                {t("subscriptionPage.audience.individual")}
                            </button>
                            <button
                                type="button"
                                style={{
                                    ...ui.tabBtn,
                                    ...(audience === "organization"
                                        ? ui.tabBtnActive
                                        : null),
                                }}
                                onClick={() => setAudience("organization")}
                                aria-pressed={audience === "organization"}
                            >
                                <i className="fas fa-building" />
                                {t("subscriptionPage.audience.organization")}
                            </button>
                        </div>
                    </div>
                </section>

                <section className="content">
                    {audience === "individual" && (
                        <div className="row justify-content-center">
                            <div className="col-12 col-lg-8">
                                <h3 style={ui.colTitle}>
                                    {t("subscriptionPage.sections.individualTitle")}
                                </h3>
                                <p style={ui.colHint}>
                                    {t("subscriptionPage.sections.individualHint")}
                                </p>
                                {renderPlans(appPlans, "app")}
                            </div>
                        </div>
                    )}

                    {audience === "organization" && (
                        <div className="row justify-content-center">
                            <div className="col-12 col-lg-8">
                                <h3 style={ui.colTitle}>
                                    {t("subscriptionPage.sections.organizationTitle")}
                                </h3>
                                <p style={ui.colHint}>
                                    {t("subscriptionPage.sections.organizationHint")}
                                </p>

                                <div
                                    style={ui.orgSelectorWrap}
                                    className="kbola-org-selector"
                                >
                                    <div
                                        style={ui.orgSelectorLeft}
                                        className="kbola-org-selector-left"
                                    >
                                        <span style={ui.orgSelectorBadge}>
                                            <i className="fas fa-building" />
                                            {t("subscriptionPage.orgSelector.badge")}
                                        </span>
                                        <label
                                            htmlFor="org-subscription-select"
                                            style={ui.orgSelectorLabel}
                                        >
                                            {t("subscriptionPage.orgSelector.title")}
                                        </label>
                                        <p style={ui.orgSelectorHint}>
                                            {t("subscriptionPage.orgSelector.hint")}
                                        </p>
                                    </div>

                                    <div
                                        style={ui.orgSelectorRight}
                                        className="kbola-org-selector-right"
                                    >
                                        <div
                                            style={{
                                                ...ui.orgSelectorControl,
                                                ...(hasOrgSelected
                                                    ? ui.orgSelectorControlActive
                                                    : null),
                                                ...(orgsLoading ||
                                                myOrganizations.length === 0
                                                    ? ui.orgSelectorDisabled
                                                    : null),
                                            }}
                                        >
                                            <Select
                                                inputId="org-subscription-select"
                                                options={orgOptions}
                                                value={
                                                    orgOptions.find(
                                                        (o) =>
                                                            o.value ===
                                                            selectedOrganizationId
                                                    ) || null
                                                }
                                                onChange={(option) =>
                                                    setSelectedOrganizationId(
                                                        option?.value || null
                                                    )
                                                }
                                                placeholder={t("subscriptionPage.orgSelector.placeholder")}
                                                isDisabled={
                                                    orgsLoading ||
                                                    myOrganizations.length === 0
                                                }
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        backgroundColor: surface,
                                                        borderRadius: 999,
                                                        borderColor: hasOrgSelected
                                                            ? primary
                                                            : borderColor,
                                                        boxShadow: hasOrgSelected
                                                            ? `0 0 0 2px ${primaryGlow}`
                                                            : "none",
                                                        minHeight: 40,
                                                    }),
                                                    valueContainer: (base) => ({
                                                        ...base,
                                                        padding: "0 12px 0 14px",
                                                    }),
                                                    placeholder: (base) => ({
                                                        ...base,
                                                        color: mutedText,
                                                        fontWeight: 600,
                                                        fontSize: 13,
                                                    }),
                                                    menu: (base) => ({
                                                        ...base,
                                                        backgroundColor: surface,
                                                        borderRadius: 12,
                                                        overflow: "hidden",
                                                        zIndex: 20,
                                                    }),
                                                    option: (base, state) => ({
                                                        ...base,
                                                        backgroundColor: state.isSelected
                                                            ? primarySoft
                                                            : state.isFocused
                                                                ? alphaFromHex(primary, 0.08)
                                                                : surface,
                                                        color: state.isSelected
                                                            ? primary
                                                            : textOnSurface,
                                                        fontWeight: 600,
                                                        fontSize: 13,
                                                        cursor: "pointer",
                                                    }),
                                                    singleValue: (base) => ({
                                                        ...base,
                                                        color: primary,
                                                        fontWeight: 700,
                                                        fontSize: 13,
                                                    }),
                                                    dropdownIndicator: (base) => ({
                                                        ...base,
                                                        color: mutedText,
                                                    }),
                                                    indicatorSeparator: () => ({
                                                        display: "none",
                                                    }),
                                                    menuPortal: (base) => ({
                                                        ...base,
                                                        zIndex: 9999,
                                                    }),
                                                }}
                                                menuPortalTarget={document.body}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {renderPlans(orgPlans, "web", currentOrgPlanId)}

                                <div style={ui.note}>
                                    <div style={ui.noteHead}>
                                        <i
                                            className="fas fa-info-circle"
                                            style={{
                                                color: primary,
                                            }}
                                        />
                                        <h5 style={ui.noteTitle}>
                                            {t("subscriptionPage.customPlan.title")}
                                        </h5>
                                    </div>
                                    <div style={ui.noteBody}>
                                        {t("subscriptionPage.customPlan.body1")}
                                        <br />
                                        <br />
                                        {t("subscriptionPage.customPlan.body2")}
                                        <br />
                                        <br />
                                        {t("subscriptionPage.customPlan.body3")}{" "}
                                        <a
                                            href="mailto:info@angeonx.com"
                                            style={{
                                                color: primary,
                                            }}
                                        >
                                            info@angeonx.com
                                        </a>{" "}
                                        {t("subscriptionPage.customPlan.body4")}{" "}
                                        <span
                                            style={{
                                                color: primary,
                                            }}
                                        >
                                            {t("subscriptionPage.customPlan.freeTrial")}
                                        </span>
                                        .
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </section>
            </div>

            {featureInfoModal && (
                <div
                    style={ui.modalBackdrop}
                    role="presentation"
                    onClick={() => setFeatureInfoModal(null)}
                >
                    <div
                        style={ui.modalCard}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="subscription-feature-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div style={ui.modalHeader}>
                            <h4
                                id="subscription-feature-title"
                                style={ui.modalTitle}
                            >
                                {featureInfoModal.title}
                            </h4>
                            <button
                                type="button"
                                style={ui.modalClose}
                                aria-label="Close"
                                onClick={() => setFeatureInfoModal(null)}
                            >
                                ×
                            </button>
                        </div>
                        <div style={ui.modalBody}>
                            {featureInfoModal.description}
                        </div>
                    </div>
                </div>
            )}

            <style>{`
  #${scopeId} button:focus,
  #${scopeId} a:focus,
  #${scopeId} .btn:focus {
    outline: none;
    box-shadow: 0 0 0 0.22rem ${focusRing};
  }

  #${scopeId} .kbola-plan-card {
    transition: transform 140ms ease, box-shadow 140ms ease;
  }

  #${scopeId} .kbola-plan-card:hover {
    transform: translateY(-1px);
    box-shadow: ${planHoverShadow};
  }

  @media (max-width: 992px) {
    #${scopeId} {
      padding: 18px 12px !important;
    }
    #${scopeId} .content {
      padding: 0 !important;
    }
  }

  @media (max-width: 768px) {
    #${scopeId} .kbola-org-selector {
      flex-direction: column;
      align-items: stretch;
    }

    #${scopeId} .kbola-org-selector-left {
      margin-bottom: 10px;
    }

    #${scopeId} .kbola-org-selector-right {
      flex-basis: 100% !important;
      min-width: 0 !important;
    }
  }

  #${scopeId} .content > div {
    width: 100%;
  }

  #${scopeId} i.fas,
  #${scopeId} i.far,
  #${scopeId} i.fal,
  #${scopeId} i.fa,
  #${scopeId} i[class^="fa-"],
  #${scopeId} i[class*=" fa-"] {
    font-family: "Font Awesome 5 Free" !important;
    font-weight: 900;
  }
  #${scopeId} i.fab {
    font-family: "Font Awesome 5 Brands" !important;
    font-weight: 400;
  }
`}</style>
        </div>
    );
}
